import type { PaymentExceptionView } from "@kopi/contracts";
import {
  PAYMENT_EXCEPTION_STATUSES,
  type PaymentExceptionResolution,
  type PaymentExceptionStatus,
} from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { RadioGroup, RadioGroupItem } from "@kopi/ui/components/radio-group";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Textarea } from "@kopi/ui/components/textarea";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCheckIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { paymentExceptions } from "@/i18n/messages/payment-exceptions";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletsQuery, PAGE } from "@/lib/queries";

const ALL = "all";
const KEY = ["payment-exceptions"] as const;

/** The page's translator: its own area's keys plus the core ones. */
const useExceptionMessages = () => useMessages(paymentExceptions);
type ExceptionsT = ReturnType<typeof useExceptionMessages>["t"];

/** The status filter lives in the URL; open cases are what a manager opens the page for. */
function validateSearch(search: Record<string, unknown>): { status: PaymentExceptionStatus | typeof ALL } {
  const status = search.status;
  return status === ALL || PAYMENT_EXCEPTION_STATUSES.includes(status as PaymentExceptionStatus)
    ? { status: status as PaymentExceptionStatus | typeof ALL }
    : { status: "open" };
}

/** An outlet's cases, newest first, a page at a time (CODING_STANDARDS §4). */
const exceptionsQuery = (outletId: string, status: PaymentExceptionStatus | undefined) =>
  infiniteQueryOptions({
    queryKey: [...KEY, outletId, { status }],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId })["payment-exceptions"].get({
          query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}), ...(status ? { status } : {}) },
        }),
      ),
    enabled: Boolean(outletId),
  });

export const Route = createFileRoute("/_authed/payment-exceptions")({
  staticData: { section: "workspace", crumb: "nav.paymentExceptions" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: PaymentExceptionsPage,
});

const columnsFor = (t: ExceptionsT, money: (v: bigint) => string, when: (iso: string) => string) =>
  [
    {
      id: "case",
      header: t("paymentExceptions.case"),
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium">{t(`paymentExceptions.kind.${row.original.kind}`)}</span>
          {/* Gateway references can be long (a UUID): kept to the column, readable in full on hover. */}
          <span
            className="max-w-48 truncate text-muted-foreground text-sm md:max-w-72"
            title={row.original.gatewayReference}
          >
            {row.original.source
              ? t("paymentExceptions.reported", {
                  source: t(`paymentExceptions.source.${row.original.source}`),
                  reference: row.original.gatewayReference,
                })
              : t("paymentExceptions.reportedManual", { reference: row.original.gatewayReference })}
          </span>
        </div>
      ),
    },
    {
      id: "order",
      header: t("paymentExceptions.order"),
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.receiptNumber ?? t("paymentExceptions.noReceipt")}</span>
      ),
    },
    {
      id: "opened",
      header: t("paymentExceptions.opened"),
      cell: ({ row }) => <time dateTime={row.original.openedAt}>{when(row.original.openedAt)}</time>,
    },
    {
      id: "status",
      header: t("paymentExceptions.status"),
      meta: { className: "md:w-36" },
      cell: ({ row }) => (
        <Badge variant={row.original.status === "open" ? "warning" : "success"}>
          {t(`paymentExceptions.status.${row.original.status}`)}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: t("paymentExceptions.amount"),
      meta: { align: "end" },
      cell: ({ row }) => <span className="tabular-nums">{money(BigInt(row.original.amount))}</span>,
    },
  ] satisfies DataTableColumn<PaymentExceptionView>[];

/**
 * QRIS reconciliation (R1-26): payments the gateway confirmed that posted nothing — late successes
 * (AC-08) and overpayments after a cash override (AC-37). A manager accepts a late success as the
 * order's payment, or records that the money went back to the customer.
 */
function PaymentExceptionsPage() {
  const { t, format } = useExceptionMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const reconcilable = outlets.filter((o) => canAt(scopes, "payments.reconcile", o.id));
  const [outletId, setOutletId] = useState(
    reconcilable.find((o) => o.id === current?.id)?.id ?? reconcilable[0]?.id ?? "",
  );
  const { status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useInfiniteQuery(exceptionsQuery(outletId, status === ALL ? undefined : status));
  const [resolving, setResolving] = useState<PaymentExceptionView | null>(null);
  const columns = useMemo(
    () =>
      columnsFor(
        t,
        (v) => format.money(v),
        (iso) => format.dateTime(iso),
      ),
    [t, format],
  );

  if (reconcilable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("nav.paymentExceptions")} subtitle={t("paymentExceptions.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={t("nav.paymentExceptions")} subtitle={t("paymentExceptions.subtitle")} />
      <DataTable
        paging="pages"
        label={t("paymentExceptions.list")}
        columns={columns}
        query={list}
        getRowId={(c) => c.id}
        filters={
          <>
            {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
            <OptionSelect
              aria-label={t("outlet.heading")}
              className="md:w-56"
              options={reconcilable.map((o) => ({ value: o.id, label: o.name }))}
              value={outletId}
              onValueChange={setOutletId}
            />
            <OptionSelect
              aria-label={t("paymentExceptions.status")}
              className="md:w-56"
              options={[
                { value: ALL, label: t("paymentExceptions.status.all") },
                ...PAYMENT_EXCEPTION_STATUSES.map((s) => ({
                  value: s,
                  label: t(`paymentExceptions.status.${s}`),
                })),
              ]}
              value={status}
              onValueChange={(value) =>
                void navigate({
                  search: { status: value as PaymentExceptionStatus | typeof ALL },
                  replace: true,
                })
              }
            />
          </>
        }
        rowActions={(c) =>
          c.status === "open"
            ? [
                {
                  label: t("paymentExceptions.resolve"),
                  icon: <CheckCheckIcon />,
                  onSelect: () => setResolving(c),
                },
              ]
            : []
        }
        empty={{ title: t("paymentExceptions.emptyTitle"), description: t("paymentExceptions.emptyHint") }}
      />
      {resolving && <ResolveDialog exception={resolving} onClose={() => setResolving(null)} />}
    </Page>
  );
}

/** Resolves one case: accept a late success as the payment, or record the manual refund. */
function ResolveDialog(props: { exception: PaymentExceptionView; onClose: () => void }) {
  const { exception } = props;
  const { t, format } = useExceptionMessages();
  const queryClient = useQueryClient();
  const ids = { resolution: useId(), note: useId(), reference: useId() };
  const duplicate = exception.kind === "duplicate_reference";
  const [resolution, setResolution] = useState<PaymentExceptionResolution>(
    exception.kind === "overpayment" ? "refunded" : duplicate ? "rejected" : "accepted",
  );
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  // Accepting a duplicate (R1-28) needs the payment's own reference, as the merchant account shows it.
  const needsReference = duplicate && resolution === "accepted";
  const cleanReference = reference.replace(/\s+/g, "").toUpperCase();
  const valid =
    note.trim().length >= 3 && (!needsReference || /^[A-Z0-9][A-Z0-9._/-]{3,63}$/.test(cleanReference));
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1["payment-exceptions"]({ exceptionId: exception.id }).resolve.post({
          resolution,
          note: note.trim(),
          expectedRevision: exception.revision,
          ...(needsReference ? { merchantReference: cleanReference } : {}),
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(t("paymentExceptions.resolved"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  const code = save.error instanceof ApiRequestError ? save.error.code : null;
  const options: PaymentExceptionResolution[] =
    exception.kind === "late_success"
      ? ["accepted", "refunded"]
      : duplicate
        ? ["rejected", "accepted"]
        : ["refunded"];
  /** A duplicate's acceptance reads differently: it is paid under its own reference. */
  const optionKey = (option: PaymentExceptionResolution) =>
    duplicate && option === "accepted" ? "acceptedOwn" : option;
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("paymentExceptions.resolveTitle")}
      description={`${t(`paymentExceptions.kind.${exception.kind}`)} · ${format.money(BigInt(exception.amount))}`}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="resolve-exception" disabled={!valid || save.isPending}>
          {t(save.isPending ? "paymentExceptions.saving" : "paymentExceptions.save")}
        </Button>
      }
    >
      <form id="resolve-exception" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <p className="text-muted-foreground text-sm">{t(`paymentExceptions.kindHint.${exception.kind}`)}</p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium text-sm">{t("paymentExceptions.resolution")}</legend>
          <RadioGroup
            value={resolution}
            onValueChange={(value) => setResolution(value as PaymentExceptionResolution)}
            className="flex flex-col gap-3"
          >
            {options.map((option) => (
              <div key={option} className="flex items-start gap-3">
                <RadioGroupItem id={`${ids.resolution}-${option}`} value={option} className="mt-1" />
                <Label
                  htmlFor={`${ids.resolution}-${option}`}
                  className="flex flex-col items-start gap-0.5 font-normal"
                >
                  <span className="font-medium">
                    {t(`paymentExceptions.resolution.${optionKey(option)}`)}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {t(`paymentExceptions.resolution.${optionKey(option)}Hint`)}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </fieldset>
        {needsReference && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.reference}>{t("paymentExceptions.ownReference")}</Label>
            <Input
              id={ids.reference}
              maxLength={80}
              autoComplete="off"
              spellCheck={false}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <p className="text-muted-foreground text-sm">{t("paymentExceptions.ownReferenceHint")}</p>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.note}>{t("paymentExceptions.note")}</Label>
          <Textarea
            id={ids.note}
            maxLength={500}
            value={note}
            placeholder={t("paymentExceptions.notePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              code === "exception_revision_conflict" || code === "exception_resolved"
                ? "paymentExceptions.error.changed"
                : code === "already_paid"
                  ? "paymentExceptions.error.alreadyPaid"
                  : code === "reference_taken"
                    ? "paymentExceptions.error.referenceTaken"
                    : "paymentExceptions.error.failed",
            )}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
